((L, U) => {
	'use strict';

	const filters = {
		num(seg) {
			return /^\d+$/.test(seg);
		}
	};
	const settings = {};

	let any = [];
	let routes;
	let segs;
	let uri;

	/**
	 * Recursively process routes
	 *
	 * @private
	 * @param {Object} route - route to evaluate
	 * @param {Number} i - current index in iteration
	 * @param {Number} total - total number of routes
	 * @param {String} [event='load'] - lifecycle event
	 * @param {Array} [parent] - parent route values
	 */
	const _process = (route, i, total, event, parent) => {
		const keys = Object.keys(route);

		let segment = segs[i];

		i++;

		// Match against patterns
		for (let x = 0; x < keys.length; x++) {
			const key = keys[x];
			const child = route[key];
			const options = key.split('||');
			const object = L.$isObject(child);

			let k = 0;
			let y = i;
			let match = false;
			let ran = false;
			let option;

			for (; k < options.length; k++) {
				option = options[k];

				const parts = option.split(':');
				const history = event !== U;

				let negate = false;
				let push = false;
				let eq = false;

				// Ensure event type matches route type
				if (
					(! history && (
						parts.includes('unload')
					)) ||
					(history && (
						! object &&
						! parts.includes(event)
					))
				) {
					continue;
				}

				// Set option to rule root
				if (parts.length > 1) {
					option = parts[0];

					if (! option && parent) {
						option = parent[0];
						segment = parent[1];

						y--;
					}
				}

				// Negate routes prefixed by !
				if (option[0] === '!') {
					option = option.slice(1);
					negate = true;
				}

				// Move the segment pointer back one level
				if (parts.includes('eval')) {
					y--;
				}

				if (option === segment) {
					eq = true;
				} else if (option[0] === '$') {
					option = option.slice(1);

					if (option === 'any') {
						eq = true;

						if (parts.includes('fire')) {
							ran = true;
						} else if (! object) {
							push = true;
						}
					} else if (option === 'root') {
						if (! segment) {
							eq = true;
							ran = true;
						}
					} else if (option[0] === '/') {
						const split = option.split('/');

						if (new RegExp(split[1], split[2] || U).test(segment)) {
							eq = true;
						}
					} else {
						const filter = filters[option];

						if (filter) {
							if (filter(segment, child, y) === true) {
								eq = true;
								push = true;
							}
						} else if (segment && segment.trim() !== '') {
							eq = true;
						}
					}

					option = '$' + option;
				}

				// Invert the equality if the route is negated
				if (negate) {
					eq = ! eq;
				}

				if (eq) {
					// If ran is true then execute the route immediately
					if (ran && ! object) {
						L.$exec(child, {
							args: segment
						});
					}

					// If push is true then push the route to "any" queue
					if (push) {
						ran = true;

						any.push([
							child,
							segment
						]);
					}

					// Remove the route if set to once
					if (parts.includes('once')) {
						delete route[key];
					}

					// Set match to true and break on match
					match = true;

					break;
				}
			}

			// If matched then process recursively or execute if applicable
			if (match) {
				if (object) {
					_process(child, y, total, event, [
						option,
						segment
					]);
				} else if (! ran && y === total) {
					L.$exec(child, {
						args: segment
					});
				}
			}
		}
	};

	L.routes = {
		/**
		 * Set routing options
		 *
		 * @param {Object|String} a - name or settings object
		 * @param {*} [b] - value
		 */
		config(a, b) {
			L._extend(settings, a, b);
		},

		/**
		 * Set current URI values
		 *
		 * @param {Object|String} [value]
		 * @param {String} [value.full]
		 * @param {String} [value.hash]
		 * @param {Boolean} [value.history]
		 * @param {String} [value.path]
		 * @param {Object} [value.query]
		 * @param {String} [value.raw]
		 * @param {Array} [value.segments]
		 * @param {String} [value.url]
		 * @param {Boolean} [history=false]
		 * @returns {Object}
		 */
		set(value, history) {
			uri = L.$isObject(value) ?
				Object.assign(this.uri(), value) :
				this.parse(value);

			if (history) {
				uri.history = true;
			}

			return uri;
		},

		/**
		 * Get current URI values
		 *
		 * @returns {Object} data
		 */
		uri() {
			if (uri) {
				const hash = L._loc.hash;

				uri.hash = hash.slice(1);
				uri.raw = uri.full + hash;

				return uri;
			}

			return this.parse();
		},

		/**
		 * Parse a URL string into parts
		 *
		 * @param {String} [uri]
		 * @returns {Object}
		 */
		parse(uri) {
			const a = L._doc.createElement('a');

			a.href = uri || L._loc.href;

			const search = a.search;
			const path = a.pathname.replace(/^\/|\/$/g, '');

			return {
				full: '/' + path + search,
				hostname: a.hostname,
				hash: a.hash.slice(1),
				history: false,
				path: '/' + path,
				query: search ?
					L.$unserialize(search) : {},
				raw: '/' + path + search + a.hash,
				segments: path.split('/'),
				url: a.href
			};
		},

		/**
		 * Get all segments or segment at index
		 *
		 * @param {Number} [index]
		 * @returns {Array|String} segments
		 */
		segments(index) {
			const segs = this.uri().segments;

			return index !== U ?
				(segs[index] || '') : segs;
		},

		/**
		 * Retrieve or add route endpoints
		 *
		 * @param {Object} obj - routes
		 * @param {Boolean} [init=false] - immediately evaluate routes
		 * @returns {Object} routes
		 */
		map(obj, init) {
			const current = routes || {};

			if (obj) {
				routes = L.$extend(true, current, obj);

				if (init) {
					this.run({
						routes: routes
					});
				}
			}

			return current;
		},

		/**
		 * Add conditional route filter
		 *
		 * @param {Object|String} a - name or filter object
		 * @param {Function} [b]
		 */
		addFilter(a, b) {
			L._extend(filters, a, b);
		},

		/**
		 * Evaluate routes against URI
		 *
		 * @param {Object} [options]
		 * @param {String} [options.event='load']
		 * @param {String} [options.path]
		 * @param {Object} [options.routes]
		 */
		run(options = {}) {
			if (settings.before && settings.before(options) === false) {
				return;
			}

			const current = options.routes || routes;

			if (current) {
				segs = options.path ?
					options.path.replace(/^\/|\/$/g, '')
						.split(/[#?]/)[0]
						.split('/') :
					this.segments();

				_process(current, 0, segs.length, options.event);

				// Execute queued init functions on last iteration
				if (any.length) {
					for (let i = 0; i < any.length; i++) {
						const rule = any[i];

						L.$exec(rule[0], {
							args: rule[1]
						});
					}

					// Clear queue for next iteration
					any = [];
				}
			}
		}
	};
})(Apt, undefined);